// D-041 (2026-05-21): shared helper for the M-PACT composite score.
//
// Extracted from client/src/components/helix/OperationalHelix.tsx so the
// AccountScorecard Overview composite widget can show the same live score
// the Helix tab computes. The two surfaces must agree, hence one source of
// truth.
//
// The functions are intentionally local-storage backed (matching the
// Helix's own persistence). When Option-A migrates Helix answers to
// Supabase, this lib swaps to the same async store.

import { ALL_QUESTIONS } from "@shared/questions";

export interface CpAnswer {
  selectedOption?: string | null;
}
export type AnswerStore = Record<string, CpAnswer>;

function lsKey(id: string): string {
  return `aip:helix:${id}`;
}

export function loadHelixAnswers(accountId: string): AnswerStore {
  try {
    const raw = localStorage.getItem(lsKey(accountId));
    return raw ? (JSON.parse(raw) as AnswerStore) : {};
  } catch {
    return {};
  }
}

function resolveOptionPoints(
  q: typeof ALL_QUESTIONS[number],
  storedValue: string,
): number | null {
  const maxPoints = Math.max(...q.options.map((o) => o.points));
  const asNumber = parseInt(storedValue, 10);
  if (!Number.isNaN(asNumber) && /^[0-9]+$/.test(storedValue)) {
    return Math.max(0, Math.min(asNumber, maxPoints));
  }
  const opt = q.options.find((o) => o.key === storedValue);
  if (!opt) return null;
  return Math.max(0, Math.min(opt.points, maxPoints));
}

/**
 * Wave 1 weighted-CP composite — mirrors server-side recompute_account_score().
 *
 * For each CP 1..10:
 *   cp_pct = SUM(points answered) / SUM(max-of-options for answered questions) * 100
 *   If no questions answered in CP, cp_pct contributes 0 (but weight still consumed).
 * composite = SUM(cp_pct * 0.10) / 1.0
 *
 * Equal 0.10 weight per CP — Wave 2 swaps these for canonical dimension weights.
 * Empty CPs consume their weight share, so partial coverage is honestly reflected.
 */
export function computeCompositeScore(answers: AnswerStore): number {
  const CP_WEIGHT = 0.1;
  const TOTAL_WEIGHT = 1.0;
  let weightedSum = 0;

  for (let cp = 1; cp <= 10; cp++) {
    const cpQs = ALL_QUESTIONS.filter((q) => q.cp === cp);
    let sumPoints = 0;
    let sumMax = 0;

    for (const q of cpQs) {
      const opt = answers[q.key]?.selectedOption;
      if (opt === null || opt === undefined) continue;
      const pts = resolveOptionPoints(q, opt);
      if (pts === null) continue;
      const qMax = Math.max(...q.options.map((o) => o.points));
      sumPoints += pts;
      sumMax += qMax;
    }

    if (sumMax > 0) {
      const cpPct = (sumPoints / sumMax) * 100;
      weightedSum += cpPct * CP_WEIGHT;
    }
  }

  const composite = weightedSum / TOTAL_WEIGHT;
  return Math.max(0, Math.min(100, Math.round(composite)));
}
